import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Receipt, Check } from "lucide-react";

const FROM = "2026-06-01"; // nu mai devreme de 1 iunie
const fmtD = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join(".") : "");

export default function UnbilledBanner() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  const load = () => api.get("/billing/todo").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  const invoiceProject = async (p) => {
    if (!window.confirm(`Marchezi proiectul „${p.name}" ca FACTURAT?`)) return;
    setBusyKey(p.id);
    try {
      await api.post(`/billing/invoice/project/${p.id}`, {});
      toast.success(`„${p.name}" marcat ca facturat`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
    finally { setBusyKey(null); }
  };

  const invoiceRegieWeek = async (r) => {
    if (!window.confirm(`Marchezi săptămâna ${r.week_label} de la „${r.name}" ca FACTURATĂ?`)) return;
    const key = `${r.regie_id}|${r.week_start}`;
    setBusyKey(key);
    try {
      await api.post(`/billing/invoice/regie/${r.regie_id}/week/${r.week_start}`, {});
      toast.success(`„${r.name}" ${r.week_label} — facturată`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
    finally { setBusyKey(null); }
  };

  if (!data) return null;
  const projects = (data.projects_pending || []).filter((p) => {
    const d = (p.dismantled_at || p.end_date || "").slice(0, 10);
    return !d || d >= FROM;
  });
  const regii = (data.regii_pending || []).filter((r) => (r.week_start || "") >= FROM);
  const total = projects.length + regii.length;
  if (total === 0) return null;

  return (
    <div className="border border-violet-300 bg-violet-50 rounded-lg overflow-hidden" data-testid="unbilled-banner">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="unbilled-toggle"
      >
        <div className="flex items-center gap-2">
          <Receipt size={18} className="text-violet-600" />
          <span className="font-bold text-violet-900 text-sm">
            🧾 Nefacturate (de la 1 iunie): {total}
            <span className="ml-2 text-xs font-semibold text-violet-700">
              {projects.length} {projects.length === 1 ? "proiect" : "proiecte"} · {regii.length} {regii.length === 1 ? "săptămână regie" : "săptămâni regii"}
            </span>
          </span>
        </div>
        {open ? <ChevronUp size={16} className="text-violet-700" /> : <ChevronDown size={16} className="text-violet-700" />}
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-2">
          {projects.map((p) => (
            <div key={p.id} className="bg-white border border-violet-200 rounded-md px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`unbilled-project-${p.id}`}>
              <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">Proiect</span>
              <div className="min-w-[160px]">
                <span className="font-bold text-sm text-zinc-900">{p.name}</span>
                {p.adresa && <div className="text-[11px] text-zinc-500">📍 {p.adresa}</div>}
              </div>
              {(p.dismantled_at || p.end_date) && (
                <span className="text-xs text-zinc-600">Finalizat: {fmtD(p.dismantled_at || p.end_date)}</span>
              )}
              <button
                onClick={() => invoiceProject(p)}
                disabled={busyKey === p.id}
                className="ml-auto inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50 flex-shrink-0"
                data-testid={`invoice-project-${p.id}`}
                title="Marchează proiectul ca facturat"
              >
                <Check size={13} /> {busyKey === p.id ? "..." : "Facturat"}
              </button>
            </div>
          ))}
          {regii.map((r) => (
            <div key={`${r.regie_id}|${r.week_start}`} className="bg-white border border-violet-200 rounded-md px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`unbilled-regie-${r.regie_id}-${r.week_start}`}>
              <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Regie</span>
              <div className="min-w-[160px]">
                <span className="font-bold text-sm text-zinc-900">{r.name}</span>
                {r.firma && <span className="text-[11px] text-zinc-500 ml-1">({r.firma})</span>}
              </div>
              <span className="text-xs text-zinc-600">{r.week_label} ({fmtD(r.week_start)}–{fmtD(r.week_end)})</span>
              {r.total_hours > 0 && <span className="text-xs font-semibold text-violet-800">⏱ {r.total_hours}h</span>}
              {r.total_amount_chf != null && <span className="text-xs font-bold text-emerald-700">{r.total_amount_chf.toLocaleString("de-CH")} CHF</span>}
              <button
                onClick={() => invoiceRegieWeek(r)}
                disabled={busyKey === `${r.regie_id}|${r.week_start}`}
                className="ml-auto inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50 flex-shrink-0"
                data-testid={`invoice-regie-${r.regie_id}-${r.week_start}`}
                title="Marchează săptămâna ca facturată"
              >
                <Check size={13} /> {busyKey === `${r.regie_id}|${r.week_start}` ? "..." : "Facturat"}
              </button>
            </div>
          ))}
          <div className="pt-1">
            <Link to="/facturare" className="text-xs font-bold text-violet-700 hover:underline" data-testid="unbilled-goto-billing">
              → Mergi la Facturare
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
